import QueryString from "qs";
import { useEffect, useReducer, useState } from "react";
import { Edit } from "react-feather";
import { toast } from "react-hot-toast";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, Card, Col, Row } from "reactstrap";
import DataGrid from "../../components/dataGrid/DataGrid";
import FormInput from "../../components/FormInput";
import FormSelect from "../../components/FormSelect";
import { CityData, getCities } from "../../services/city.service";
import { getStates } from "../../services/state.service";

const initialState = {
  data: [],
  page: 1,
  limit: 100,
  totalPages: 1,
  totalResults: 0,
  startIndex: 1,
  endIndex: 100,
};

function reducer(state: any, action: any) {
  switch (action.type) {
    case "SET_DATA":
      return action.payload;
    default:
      return state;
  }
}

function Cities() {
  const [filterData, setFilterData] = useState({
    search: "",
    state: "",
  });
  const [state, setState] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, dispatch] = useReducer(reducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  const setData = (data: any) => {
    dispatch({ type: "SET_DATA", payload: data });
  };

  useEffect(() => {
    const { page, state } = query;
    setLoading(true);
    getCities({ page, search: filterData.search, state })
      .then((response: any) => {
        setData(response.data);
      })
      .catch(() => {
        toast.error("Failed to fetch cities");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [location.search]);

  const columns = [
    {
      selector: (row: any) => row.serialNumber,
      name: "S.No.",
      sortable: false,
      maxWidth: "70px",
      minWidth: "70px",
    },
    {
      selector: (row: CityData) => row.name,
      name: "City Name",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: any) => row.state?.name || "N/A",
      name: "State",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: any) => (
        <>
          <Link
            className='me-1'
            to={`edit/${row.id}?${QueryString.stringify({
              redirect: location.pathname + location.search,
            })}`}
          >
            <Edit size={15} />
          </Link>
        </>
      ),
      name: "Actions",
      sortable: false,
      center: true,
      maxWidth: "90px",
      minWidth: "90px",
    },
  ];

  const handleFilter = () => {
    const queryParams: any = { ...query, page: 1, ...filterData };
    Object.keys(queryParams).forEach((key) => {
      if (!queryParams[key]) {
        delete queryParams[key];
      }
    });

    const queryString = QueryString.stringify(queryParams);
    navigate(`${location.pathname}${Object.keys(queryParams).length > 0 ? "?" + queryString : ""}`);
  };

  const handleClearFilter = () => {
    setState("");
    setFilterData({ search: "", state: "" });
    const queryParams = { page: 1 };
    const queryString = QueryString.stringify(queryParams);
    navigate(`${location.pathname}?${queryString}`);
  };

  return (
    <div className='d-flex flex-column h-100 px-sm-1 w-100 pb-sm-1'>
      <Row className='align-items-center mb-2 page-title-row'>
        <Col sm>
          <h1 className='my-1 fw-bold h4'>City Master</h1>
        </Col>
        <Col sm='auto'>
          <div className='justify-content-between'>
            <Link
              to={`/cities/add?${QueryString.stringify({
                redirect: location.pathname + location.search,
              })}`}
              className='btn btn-sm btn-primary my-1 text-white'
            >
              Add New
            </Link>
          </div>
        </Col>
      </Row>

      <div className='flex-grow-1 overflow-hidden' style={{ height: "calc(100vh - 230px)" }}>
        <Card className='h-100 p-3 w-100'>
          <div className='d-flex flex-column h-100 rounded-0 list-data-grid-wrapper'>
            <DataGrid
              columns={columns}
              data={data}
              loading={loading}
              isFilterApplied={Object.keys(query).some((key) => key !== "page")}
              noDataTitle='No cities found'
              noDataSubtitle='There are no cities available right now.'
              noDataFilteredTitle='No cities match your filters'
              noDataFilteredSubtitle='Try changing or clearing the filter criteria.'
              FilterSubHeader={true}
              filterData={filterData}
              setFilterData={setFilterData}
              subHeaderComponent={
                <Row className='w-100 align-items-start g-3 py-1 px-1'>
                  <Col md={2}>
                    <FormInput
                      name='search'
                      label='Name'
                      placeholder='Search by name'
                      onChange={(e) =>
                        setFilterData({
                          ...filterData,
                          [e.target.name]: e.target.value,
                        })
                      }
                      value={filterData.search}
                    />
                  </Col>
                  <Col md={2}>
                    <FormSelect
                      id='state'
                      name='state'
                      label='State'
                      placeholder='State'
                      isAsync
                      isClearable
                      getOptions={getStates}
                      value={state}
                      onChange={(name: any, value: any) => {
                        setState(value);
                        setFilterData({
                          ...filterData,
                          [name]: value?.id,
                        });
                      }}
                    />
                  </Col>

                  <Col md='auto'>
                    <div className='filter-action-buttons'>
                      <Button color='primary' size='sm' onClick={() => handleFilter()}>
                        Filter
                      </Button>
                      <Button color='danger' size='sm' onClick={() => handleClearFilter()}>
                        Clear
                      </Button>
                    </div>
                  </Col>
                </Row>
              }
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

export default Cities;
